<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { api } from '../services/api'
import { store } from '../store'

const route = useRoute()
const subject = ref(null)
const semester = ref(1)
const openChapters = ref(new Set([0]))
const error = ref('')

const activeSemester = computed(() => subject.value?.semesters.find(item => item.number === semester.value))
// Bài nên học tiếp: bài đang dở đầu tiên, nếu không có thì bài chưa học đầu tiên.
const resume = computed(() => {
  const all = (subject.value?.semesters || []).flatMap(sem => sem.chapters.flatMap((chapter, index) => chapter.lessons.map(lesson => ({ lesson, semester: sem.number, chapterIndex: index }))))
  return all.find(item => item.lesson.status === 'in_progress') || all.find(item => item.lesson.status === 'not_started' && !item.lesson.exam_quiz_id)
})
const doneCount = chapter => chapter.lessons.filter(lesson => lesson.status === 'completed').length

function toggleChapter(index) {
  const next = new Set(openChapters.value)
  next.has(index) ? next.delete(index) : next.add(index)
  openChapters.value = next
}

async function load() {
  subject.value = null
  try {
    const data = await api(`/subjects/${route.params.slug}`)
    subject.value = data.subject
    semester.value = resume.value?.semester || data.subject.semesters[0]?.number || 1
    openChapters.value = new Set([resume.value?.chapterIndex || 0])
  } catch (err) { error.value = err.message }
}

onMounted(load)
watch(() => route.params.slug, load)
</script>

<template>
  <div v-if="!subject && !error" class="loading-block"><div><div class="loader"></div><p>Đang xếp chương trình học…</p></div></div>
  <div v-else-if="error" class="error-banner">{{ error }}</div>
  <template v-else>
    <div class="breadcrumbs"><RouterLink to="/mon-hoc">Môn học</RouterLink><span>›</span><span>{{ subject.title }}</span></div>
    <section class="card subject-hero">
      <div class="subject-icon" :class="subject.color">{{ subject.icon }}</div>
      <div class="subject-hero-copy"><p class="eyebrow">{{ subject.series }} · Lớp {{ subject.grade }}</p><h1>{{ subject.title }}</h1><p>{{ subject.description }}</p></div>
      <div class="subject-total"><b>{{ subject.progress.percent }}% hoàn thành</b><div class="progress-track"><span :style="{ width: `${subject.progress.percent}%` }"></span></div><RouterLink v-if="store.user.role === 'student' && resume" :to="`/bai-hoc/${resume.lesson.id}`" class="btn btn-primary subject-resume">{{ resume.lesson.status === 'in_progress' ? 'Học tiếp' : 'Học bài tiếp theo' }} →<small>{{ resume.lesson.title }}</small></RouterLink></div>
    </section>

    <div class="semester-tabs">
      <button v-for="item in subject.semesters" :key="item.id" :class="{ active: semester === item.number }" @click="semester = item.number; openChapters = new Set([0])">{{ item.title }}</button>
    </div>

    <section class="curriculum-list">
      <article v-for="(chapter, index) in activeSemester?.chapters" :key="chapter.id" class="card chapter-card" :class="{ open: openChapters.has(index) }">
        <button class="chapter-head" @click="toggleChapter(index)">
          <span class="chapter-number">{{ String(index + 1).padStart(2, '0') }}</span>
          <div><h3>{{ chapter.title }}</h3><p>{{ chapter.description }}</p></div>
          <span class="chapter-count" :class="{ done: chapter.lessons.length && doneCount(chapter) === chapter.lessons.length }">{{ store.user.role === 'student' ? `${doneCount(chapter)}/${chapter.lessons.length} bài` : `${chapter.lessons.length} bài` }}</span><span class="chevron">⌄</span>
        </button>
        <div v-if="openChapters.has(index)" class="chapter-body">
          <template v-for="lesson in chapter.lessons" :key="lesson.id">
          <div v-if="lesson.exam_quiz_id" class="lesson-row exam-row">
            <span class="lesson-state" :class="{ done: lesson.status === 'completed' }">{{ lesson.status === 'completed' ? '✓' : '✎' }}</span>
            <div><strong>{{ lesson.title }}</strong><small>{{ lesson.duration_minutes }} phút · {{ lesson.question_count }} câu<template v-if="lesson.exam_structure"> · {{ lesson.exam_structure }}</template></small></div>
            <span class="exam-actions"><RouterLink :to="`/bai-hoc/${lesson.id}`" class="text-link">Xem hướng dẫn</RouterLink><RouterLink v-if="store.user.role === 'student'" :to="`/luyen-tap/${lesson.exam_quiz_id}`" class="chip orange">Làm bài kiểm tra →</RouterLink></span>
          </div>
          <RouterLink v-else :to="`/bai-hoc/${lesson.id}`" class="lesson-row">
            <span class="lesson-state" :class="{ done: lesson.status === 'completed', active: lesson.status === 'in_progress' }">{{ lesson.status === 'completed' ? '✓' : lesson.status === 'in_progress' ? '•' : '' }}</span>
            <div><strong>{{ lesson.title }}</strong><small>{{ lesson.duration_minutes }} phút · {{ lesson.question_count }} câu luyện nhanh · {{ lesson.status === 'completed' ? 'Đã hoàn thành' : lesson.status === 'in_progress' ? `Đang học ${lesson.progress}%` : 'Chưa bắt đầu' }}</small></div>
            <span class="text-link">{{ lesson.status === 'not_started' ? 'Bắt đầu' : lesson.status === 'completed' ? 'Xem lại' : 'Học tiếp' }} →</span>
          </RouterLink>
          </template>
          <RouterLink v-for="quiz in chapter.quizzes" :key="quiz.id" :to="`/luyen-tap/${quiz.id}`" class="quiz-row">
            <div><strong>✎ {{ quiz.title }}</strong><small>{{ quiz.duration_minutes }} phút · {{ quiz.question_count }} câu hỏi</small></div><span class="chip orange">Luyện tập →</span>
          </RouterLink>
        </div>
      </article>
    </section>
  </template>
</template>
